import io from 'socket.io-client';
import Vue from 'vue';
import store from '@/store';
import { LOGOUT } from '@/store/modules/user/types';
import {
  SET_UNREAD_NUMBER,
  READ_MESSAGE,
  SET_USERS_NUMBER,
  SET_RECONNECTION_ATTEMPTS
} from '@/store/modules/chats/types';
import { UPDATE_ACTIVITY_STATUS } from '@/store/modules/users/types';

const clearLocalStorage = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('session_group_id');
};

const socket = io(process.env.VUE_APP_API, {
  path: '/ws',
  transports: ['websocket'],
  autoConnect: false,
  reconnectionDelay: 5000
});

socket.on('connect', () => {
  Vue.prototype.$eventHub.$emit('socketConnected');
});

socket.on('disconnect', reason => {
  if (reason === 'io server disconnect') {
    socket.connect();
  }

  if (reason === 'transport close') {
    clearLocalStorage();
    window.location = `${process.env.VUE_APP_AUTH}?signin`;
  }

  if (reason === 'io client disconnect') {
    store.commit(`user/${LOGOUT}?signin`);
  }
});

socket.on('connect_error', error => {
  console.error('IO connection error', error);
});

socket.on('error', error => {
  console.error('IO error', error);
});

socket.on('reconnecting', attempt => {
  store.commit(`chats/${SET_RECONNECTION_ATTEMPTS}`, attempt);
});

socket.on('reconnect', () => {
  store.commit(`chats/${SET_RECONNECTION_ATTEMPTS}`, 0);
});

socket.on('active_at', data => {
  store.commit(`users/${UPDATE_ACTIVITY_STATUS}`, data);
});

const handleNewMessage = message => {
  Vue.prototype.$eventHub.$emit('newMessage', message);
};

const onErrors = data => {
  if (data.length) data.forEach(err => console.error(err));
};

socket.on('errors', onErrors);

const onUnreadNumber = data => {
  store.commit(`chats/${SET_UNREAD_NUMBER}`, data);
};

const onReadMessage = data => {
  store.commit(`chats/${READ_MESSAGE}`, data);
};

const onChatJoined = data => {
  Vue.prototype.$eventHub.$emit('chatJoined', data);
};

const onChatUsersNumber = data => {
  store.commit(`chats/${SET_USERS_NUMBER}`, data);
};

const handleRefreshSessionResponse = (response, successCb) => {
  if (response.errors) {
    socket.disconnect();
    clearLocalStorage();
    window.location = process.env.VUE_APP_AUTH;
  } else {
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);

    successCb();
  }
};

socket.on('message', handleNewMessage);
socket.on('unread-number', onUnreadNumber);
socket.on('errors', onErrors);
socket.on('read-message', onReadMessage);
socket.on('chat-joined', onChatJoined);
socket.on('chat-users-number', onChatUsersNumber);

socket.refreshSession = successCb => {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  socket.emit('refresh-token', {
    access_token: accessToken, refresh_token: refreshToken
  }, response => handleRefreshSessionResponse(response, successCb));
};

socket.signin = (successCb = () => {}, errorCb = () => {}) => {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  socket.emit('signin', { access_token: accessToken }, data => {
    if (data.errors) {
      if (data.errors.some(err => err.code && err.code === 10)) {
        socket.emit('refresh-token', { access_token: accessToken, refresh_token: refreshToken }, response => {
          if (response.errors) {
            socket.disconnect();
            clearLocalStorage();

            window.location = process.env.VUE_APP_AUTH;
          } else {
            console.warn(`Expired token ${accessToken} has been refreshed`);

            localStorage.setItem('access_token', response.access_token);
            localStorage.setItem('refresh_token', response.refresh_token);

            socket.signin(successCb, errorCb);
          }
        });
      } else {
        errorCb(data.errors);
      }
    } else {
      successCb(data);
    }
  });
};

socket.logout = sessionId => {
  socket.emit('logout', { group_session_id: sessionId });
  clearLocalStorage();

  window.location = process.env.VUE_APP_AUTH;
};

export default socket;
